/**
 * Navbar Component
 * Refined Legal Atelier — gold bookmark-edge accents,
 * lucide-react icons, generous spacing, premium SaaS feel.
 */

import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Box,
  Button,
  Typography,
  Avatar,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import {
  LayoutDashboard,
  Search,
  MessageSquare,
  FileText,
  Bell,
  Settings,
  Menu,
  Plus,
  ShieldCheck,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Building2,
  Rocket,
  Shield,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSubscription, FEATURES } from '../contexts/SubscriptionProvider';
import { selectIsOnboardingActive } from '../features/onboarding/redux/onboardingSelectors';
import { setOpenNavBar } from '../redux/actions/navbarAction';
import { useSnackbar } from 'notistack';
import { createConversation } from '../redux/actions/conversationAction';
import ReactGA from 'react-ga4';
import { tracker } from '../analytics';
import { useTranslation } from 'react-i18next';
import { setDocument, setSelectedCitation } from '../redux/actions/documentAction';
import { setMobileHistoryOpen } from '../redux/actions/chatUIAction';
import { ThemeToggle } from '../design-system/components';
import { History } from 'lucide-react';
import { NavbarTrialPill } from './TrialBanner';

export const MOBILE_APP_BAR_HEIGHT = 64;

const DRAWER_OPEN_WIDTH = 256;
const DRAWER_CLOSED_WIDTH = 72;
const ICON_SIZE = 18;
const ICON_STROKE = 1.75;

const Navbar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const open = useSelector((state) => state.navbar.isOpen);
  const user = useSelector((state) => state.auth?.user?.user);
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [hoveredItem, setHoveredItem] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isConversationRoute = location.pathname.startsWith('/conversation');
  const goldColor = theme.palette.primary.main;
  const isDark = theme.palette.mode === 'dark';
  const { isFeatureEnabled } = useSubscription();
  const isOnboarding = useSelector(selectIsOnboardingActive);

  const menuItems = [
    ...(isOnboarding
      ? [{ id: 'onboarding', text: t('onboarding_nav', 'Prise en main'), icon: Rocket, link: '/getting-started' }]
      : []),
    { id: 'dashboard', text: t('dashboard'), icon: LayoutDashboard, link: '/' },
    { text: t('search'), icon: Search, link: '/search' },
    { text: t('conversation'), icon: MessageSquare, link: '/conversation' },
    ...(isFeatureEnabled(FEATURES.CONTRACT_MODULE)
      ? [{ text: t('contracts'), icon: FileText, link: '/contract' }]
      : []),
    ...(isFeatureEnabled(FEATURES.NEWS_HUB)
      ? [{ text: t('legal_news'), icon: Bell, link: '/legal-news' }]
      : []),
    { text: t('entreprises'), icon: Building2, link: '/legal-notice' },
  ];

  const isItemSelected = (path) =>
    location.pathname === path ||
    location.pathname.split('/')[1] === path.split('/')[1];

  const handleMenuClick = (path, itemText) => {
    if (path === '/conversation') {
      handleCreateConversation();
      return;
    }

    ReactGA.event({
      category: 'Navigation',
      action: `Clicked on ${itemText}`,
      label: `Path: ${path}`,
    });
    tracker.event({
      category: 'Navigation',
      action: `Clicked on ${itemText}`,
      label: `Path: ${path}`,
    });
    navigate(path);
    if (isMobile) {
      dispatch(setOpenNavBar(false));
    }
  };

  const toggleDrawer = () => {
    dispatch(setOpenNavBar(!open));
  };

  const handleCreateConversation = async () => {
    try {
      const newConversation = await dispatch(createConversation(user.id));
      if (newConversation) {
        dispatch(setDocument(null));
        dispatch(setSelectedCitation(null));
        if (!isMobile) {
          enqueueSnackbar(t('create_conversation_success'), {
            variant: 'success',
          });
        }
        ReactGA.event({
          category: 'Conversation',
          action: 'User created a new conversation',
          label: `User ID: ${user.id}`,
        });
        tracker.event({
          category: 'Conversation',
          action: 'User created a new conversation',
          label: `User ID: ${user.id}`,
        });
        navigate(`/conversation/${newConversation.id}`);
      }
    } catch (error) {
      enqueueSnackbar('Failed to create conversation', { variant: 'error' });
    }
  };

  /* ─── Shared item style builder ──────────────────────────── */
  const getItemSx = (selected, hovered) => ({
    position: 'relative',
    borderRadius: '10px',
    mb: 0.25,
    overflow: 'hidden',
    backgroundColor: selected
      ? alpha(goldColor, isDark ? 0.08 : 0.06)
      : hovered
        ? alpha(theme.palette.text.primary, 0.03)
        : 'transparent',
    color: selected ? goldColor : theme.palette.text.primary,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: selected
        ? alpha(goldColor, isDark ? 0.12 : 0.09)
        : alpha(theme.palette.text.primary, 0.04),
    },
    py: 1.25,
    px: 1.5,
    /* Gold bookmark-edge accent on the left for active items */
    '&::before': selected
      ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '20%',
          bottom: '20%',
          width: '3px',
          borderRadius: '0 3px 3px 0',
          backgroundColor: goldColor,
          transition: 'all 0.25s ease-out',
        }
      : {},
  });

  /* ─── Section label ──────────────────────────────────────── */
  const SectionLabel = ({ children }) =>
    open ? (
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          px: 2,
          pt: 2,
          pb: 0.75,
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.12em',
          color: theme.palette.text.disabled,
          userSelect: 'none',
        }}
      >
        {children}
      </Typography>
    ) : null;

  return (
    <>
      {/* ═══ Mobile Top Bar ═══ */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: alpha(theme.palette.background.navbar, 0.85),
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            zIndex: theme.zIndex.drawer - 1,
            height: MOBILE_APP_BAR_HEIGHT,
            backdropFilter: 'blur(20px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
          }}
        >
          <Toolbar
            sx={{
              minHeight: MOBILE_APP_BAR_HEIGHT,
              px: { xs: 1.5, sm: 2 },
            }}
          >
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={toggleDrawer}
              sx={{
                color: theme.palette.text.primary,
                borderRadius: '10px',
                width: 40,
                height: 40,
                mr: 1,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.primary, 0.05),
                },
              }}
            >
              <Menu size={20} strokeWidth={ICON_STROKE} />
            </IconButton>

            <Box
              component={Link}
              to="/"
              onClick={(e) => {
                e.preventDefault();
                if (isMobile) dispatch(setOpenNavBar(false));
                navigate('/');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                flex: 1,
                cursor: 'pointer',
                gap: 1,
              }}
            >
              <img
                key={`mobile-full-${theme.palette.mode}`}
                src={isDark ? '/logo/logo-full-dark-mode.svg' : '/logo/logo-full.svg'}
                alt="Lawis"
                style={{ height: '40px' }}
              />
            </Box>

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isConversationRoute && (
                <IconButton
                  aria-label="Chat history"
                  onClick={() => dispatch(setMobileHistoryOpen(true))}
                  sx={{
                    color: theme.palette.text.primary,
                    borderRadius: '10px',
                    width: 40,
                    height: 40,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.text.primary, 0.05),
                    },
                  }}
                >
                  <History size={20} strokeWidth={ICON_STROKE} />
                </IconButton>
              )}
              <ThemeToggle placement="navbar" />
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* ═══ Drawer ═══ */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? open : true}
        onClose={isMobile ? toggleDrawer : undefined}
        sx={{
          width: open ? DRAWER_OPEN_WIDTH : DRAWER_CLOSED_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? DRAWER_OPEN_WIDTH : DRAWER_CLOSED_WIDTH,
            transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            overflowX: 'hidden',
            backgroundColor: theme.palette.background.navbar,
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            color: theme.palette.text.primary,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* ── Top: Logo + Collapse toggle ── */}
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: open ? 'space-between' : 'center',
              px: open ? 2 : 0,
              py: 2.5,
              minHeight: 64,
            }}
          >
            {open ? (
              <Box
                component={Link}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  '&:hover': { opacity: 0.8 },
                }}
              >
                <Box sx={{ height: 56, display: 'flex', alignItems: 'center' }}>
                  <img
                    key={`desktop-full-${theme.palette.mode}`}
                    src={isDark ? '/logo/logo-full-dark-mode.svg' : '/logo/logo-full.svg'}
                    alt="Lawis"
                    style={{ height: '56px', width: 'auto' }}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                onClick={toggleDrawer}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  '&:hover': { opacity: 0.8 },
                }}
              >
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'transparent', flexShrink: 0 }}>
                  <img
                    key={`desktop-circle-${theme.palette.mode}`}
                    src={isDark ? '/logo/logo-circle-dark-mode.svg' : '/logo/logo-circle.svg'}
                    alt="Lawis Logo"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </Avatar>
              </Box>
            )}

            {open && (
              <Tooltip title={t('collapse')} placement="right">
                <IconButton
                  onClick={toggleDrawer}
                  size="small"
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    color: theme.palette.text.disabled,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.text.primary, 0.06),
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  <ChevronsLeft size={16} strokeWidth={2} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Expand button (collapsed state) */}
        {!isMobile && !open && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
            <Tooltip title={t('expand')} placement="right">
              <IconButton
                onClick={toggleDrawer}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  color: theme.palette.text.disabled,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.06),
                    color: theme.palette.text.primary,
                  },
                }}
              >
                <ChevronsRight size={16} strokeWidth={2} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* ── Thin separator ── */}
        <Box
          sx={{
            mx: open ? 2 : 1.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        />

        {/* ── Navigation ── */}
        <Box sx={{ flex: 1, overflow: 'auto', pt: 0.5 }}>
          <SectionLabel>{t('navigation')}</SectionLabel>

          <List sx={{ py: 0, px: open ? 1 : 0.75 }}>
            {menuItems.map((item, index) => {
              const selected = isItemSelected(item.link);
              const hovered = hoveredItem === item.link;
              const Icon = item.icon;

              return (
                <Tooltip
                  key={index}
                  title={!open ? item.text : ''}
                  placement="right"
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                        fontWeight: 500,
                        borderRadius: '6px',
                        px: 1.5,
                        py: 0.75,
                      },
                    },
                  }}
                >
                  <ListItemButton
                    onClick={() => handleMenuClick(item.link, item.text)}
                    onMouseEnter={() => setHoveredItem(item.link)}
                    onMouseLeave={() => setHoveredItem(null)}
                    sx={{
                      ...getItemSx(selected, hovered),
                      justifyContent: open ? 'flex-start' : 'center',
                      px: open ? 1.5 : 0,
                      ...(!open && {
                        width: 42,
                        minWidth: 42,
                        mx: 'auto',
                      }),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: open ? 36 : 'unset',
                        justifyContent: 'center',
                        color: selected ? goldColor : theme.palette.text.secondary,
                        transition: 'color 0.2s ease',
                      }}
                    >
                      <Icon size={ICON_SIZE} strokeWidth={selected ? 2 : ICON_STROKE} />
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          sx: {
                            fontSize: '13px',
                            fontWeight: selected ? 600 : 450,
                            letterSpacing: '0.005em',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>

          {/* ── Create Conversation ── */}
          <Box sx={{ px: open ? 1.5 : 1, pt: 1.5, pb: 0.5, ...(!open && { display: 'flex', justifyContent: 'center' }) }}>
            {open ? (
              <Button
                fullWidth
                variant="contained"
                startIcon={<Plus size={16} strokeWidth={2.5} />}
                onClick={handleCreateConversation}
                className="create-conversation-btn"
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.2,
                  fontSize: '13px',
                  backgroundColor: goldColor,
                  color: '#1a1714',
                  boxShadow: 'none',
                  letterSpacing: '0.01em',
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: `0 6px 24px ${alpha(goldColor, 0.3)}`,
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: 'none',
                  },
                }}
              >
                {t('create_conversation')}
              </Button>
            ) : (
              <Tooltip title={t('create_conversation')} placement="right" arrow>
                <IconButton
                  onClick={handleCreateConversation}
                  className="create-conversation-btn"
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '10px',
                    backgroundColor: goldColor,
                    color: '#1a1714',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: `0 4px 16px ${alpha(goldColor, 0.3)}`,
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  <Plus size={18} strokeWidth={2.5} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* ── Bottom Section ── */}
        <Box sx={{ pb: 1.5 }}>
          {/* Thin separator */}
          <Box
            sx={{
              mx: open ? 2 : 1.5,
              mb: 1,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
          />

          <SectionLabel>{t('account')}</SectionLabel>

          {/* Trial status pill — only shown while in trial */}
          <NavbarTrialPill open={open} />

          {/* Theme Toggle */}
          <Box
            sx={{
              px: open ? 1.5 : 1,
              mb: 0.5,
              display: 'flex',
              justifyContent: open ? 'flex-start' : 'center',
            }}
          >
            <ThemeToggle placement="navbar" tooltipPlacement="right" fullWidth={open} />
          </Box>

          {/* Admin Panel — super_admin only */}
          {user?.roles?.includes('super_admin') && (
            <Box sx={{ px: open ? 1 : 0.75 }}>
              <Tooltip title={!open ? t('admin_panel') : ''} placement="right" arrow>
                <ListItemButton
                  onClick={() => handleMenuClick('/admin', t('admin_panel'))}
                  onMouseEnter={() => setHoveredItem('/admin')}
                  onMouseLeave={() => setHoveredItem(null)}
                  sx={{
                    ...getItemSx(isItemSelected('/admin'), hoveredItem === '/admin'),
                    justifyContent: open ? 'flex-start' : 'center',
                    px: open ? 1.5 : 0,
                    ...(!open && {
                      width: 42,
                      minWidth: 42,
                      mx: 'auto',
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: open ? 36 : 'unset',
                      justifyContent: 'center',
                      color: isItemSelected('/admin') ? goldColor : theme.palette.text.secondary,
                      transition: 'color 0.2s ease',
                    }}
                  >
                    <ShieldCheck size={ICON_SIZE} strokeWidth={isItemSelected('/admin') ? 2 : ICON_STROKE} />
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={t('admin_panel')}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: '13px',
                          fontWeight: isItemSelected('/admin') ? 600 : 450,
                          letterSpacing: '0.005em',
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </Box>
          )}

          {/* Settings */}
          <Box sx={{ px: open ? 1 : 0.75 }}>
            <Tooltip title={!open ? t('settings') : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => handleMenuClick('/settings', t('settings'))}
                onMouseEnter={() => setHoveredItem('/settings')}
                onMouseLeave={() => setHoveredItem(null)}
                sx={{
                  ...getItemSx(isItemSelected('/settings'), hoveredItem === '/settings'),
                  justifyContent: open ? 'flex-start' : 'center',
                  px: open ? 1.5 : 0,
                  ...(!open && {
                    width: 42,
                    minWidth: 42,
                    mx: 'auto',
                  }),
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: open ? 36 : 'unset',
                    justifyContent: 'center',
                    color: isItemSelected('/settings') ? goldColor : theme.palette.text.secondary,
                    transition: 'color 0.2s ease',
                  }}
                >
                  <Settings size={ICON_SIZE} strokeWidth={isItemSelected('/settings') ? 2 : ICON_STROKE} />
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={t('settings')}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '13px',
                        fontWeight: isItemSelected('/settings') ? 600 : 450,
                        letterSpacing: '0.005em',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </Box>

          {/* User Profile */}
          {user && (
            <Box
              sx={{
                mx: open ? 1.5 : 1,
                mt: 1.5,
                p: open ? 1.5 : 1,
                borderRadius: '12px',
                backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.04 : 0.025),
                border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                transition: 'all 0.2s ease',
                cursor: 'default',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: open ? 1.25 : 0,
                  justifyContent: open ? 'flex-start' : 'center',
                }}
              >
                <Avatar
                  sx={{
                    width: open ? 34 : 30,
                    height: open ? 34 : 30,
                    bgcolor: alpha(goldColor, 0.12),
                    color: goldColor,
                    fontSize: open ? '13px' : '11px',
                    fontWeight: 700,
                    flexShrink: 0,
                    border: `1.5px solid ${alpha(goldColor, 0.25)}`,
                    letterSpacing: '0.02em',
                  }}
                >
                  {user.email?.[0]?.toUpperCase()}
                </Avatar>
                {open && (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '12.5px',
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {user.first_name} {user.last_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: theme.palette.text.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.4,
                      }}
                    >
                      {user.email}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Mobile spacer for fixed app bar */}
      {isMobile && <Box sx={{ height: MOBILE_APP_BAR_HEIGHT }} />}
    </>
  );
};

export default Navbar;
